// components/GoogleAnalytics.tsx
// GA4 loader for Next.js App Router, with reliable SPA page_view tracking.
// Place <GoogleAnalytics /> inside <body> in app/layout.tsx.
"use client";

import Script from "next/script";
import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, useEffect } from "react";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// .env defines NEXT_PUBLIC_GA_MEASUREMENT_ID; accept the legacy NEXT_PUBLIC_GA_ID too.
const GA_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID ?? process.env.NEXT_PUBLIC_GA_ID ?? "G-GG0WXX0C29";

// Fires a page_view on the initial load AND on every client-side route change.
// gtag's automatic page_view is disabled (send_page_view:false) so we never double-count.
function PageViews() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (typeof window.gtag !== "function") return;
    const qs = searchParams?.toString();
    const page_path = qs ? `${pathname}?${qs}` : pathname;
    window.gtag("event", "page_view", {
      page_path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, searchParams]);

  return null;
}

export default function GoogleAnalytics() {
  if (!GA_ID) return null;

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_ID}', { send_page_view: false });
        `}
      </Script>
      {/* useSearchParams must sit under a Suspense boundary or the build de-opts. */}
      <Suspense fallback={null}>
        <PageViews />
      </Suspense>
    </>
  );
}
